import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap } from 'react-leaflet';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { CITY_COORDINATES } from '@/data/indonesianCities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { MapPin, Building2, Package } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});


const INDONESIA_CENTER = [-2.5, 118.0];
const INDONESIA_BOUNDS = [
  [-11.0, 95.0],  // Southwest
  [6.0, 141.0]    // Northeast
];

const Peta = () => {
  const [branches, setBranches] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCityBranches, setSelectedCityBranches] = useState([]);

  useEffect(() => {
    fetchGeoJSON();
    fetchData();
  }, []);

  usePeriodChange(() => fetchData());

  const fetchGeoJSON = async () => {
    try {
      // Fetch Indonesia province boundaries GeoJSON from GitHub
      const response = await fetch('https://raw.githubusercontent.com/ans-4175/peta-indonesia-geojson/master/indonesia-prov.geojson');
      const data = await response.json();
      setGeoJsonData(data);
    } catch (error) {
      console.error('Failed to fetch GeoJSON:', error);
      // Continue without GeoJSON boundaries
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      const params = periodId ? { period_id: periodId } : {};

      // Fetch branches, managers, and purchases
      const [branchesRes, managersRes, purchasesRes] = await Promise.all([
        api.get('/branches'),
        api.get('/managers'),
        api.get('/purchases', { params })
      ]);

      const allBranches = branchesRes.data;
      const allManagers = managersRes.data;
      const allPurchases = purchasesRes.data;

      // Calculate purchase weight for each branch
      const branchDataWithPurchases = allBranches.map(branch => {
        const branchPurchases = allPurchases.filter(p => p.branch_id === branch.id);
        
        // Calculate total purchase weight
        const totalWeight = branchPurchases.reduce((sum, p) => {
          const purchaseWeight = p.items ? p.items.reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0) : 0;
          return sum + purchaseWeight;
        }, 0);

        // Get coordinates for this branch location
        const coordinates = branch.location && CITY_COORDINATES[branch.location]
          ? CITY_COORDINATES[branch.location]
          : null;

        // Find manager pengelola for this branch
        const manager = allManagers.find(m => m.id === branch.manager_id);

        return {
          ...branch,
          totalWeight,
          coordinates,
          purchaseCount: branchPurchases.length,
          manager: manager,
          manager_photo_url: manager?.photo_url || null  // Use S3 URL directly
        };
      });

      // Filter branches that have coordinates
      const branchesWithCoordinates = branchDataWithPurchases.filter(b => b.coordinates);

      // Group branches by location and offset markers if multiple branches at same location
      const locationGroups = {};
      branchesWithCoordinates.forEach(branch => {
        const key = `${branch.coordinates[0]},${branch.coordinates[1]}`;
        if (!locationGroups[key]) {
          locationGroups[key] = [];
        }
        locationGroups[key].push(branch);
      });

      // Apply offset to markers at same location
      const branchesWithOffset = [];
      Object.values(locationGroups).forEach(group => {
        if (group.length === 1) {
          // Single branch at this location - no offset needed
          branchesWithOffset.push({
            ...group[0],
            offsetCoordinates: group[0].coordinates
          });
        } else {
          // Multiple branches at same location - apply circular offset
          group.forEach((branch, index) => {
            const angle = (2 * Math.PI * index) / group.length;
            const offsetDistance = 0.03; // ~3km offset
            const latOffset = offsetDistance * Math.cos(angle);
            const lngOffset = offsetDistance * Math.sin(angle);
            
            branchesWithOffset.push({
              ...branch,
              offsetCoordinates: [
                branch.coordinates[0] + latOffset,
                branch.coordinates[1] + lngOffset
              ],
              isGrouped: true,
              groupSize: group.length
            });
          });
        }
      });

      // Group data by city for the table
      const cityGrouped = {};
      branchesWithOffset.forEach(branch => {
        const city = branch.location || 'Tidak Diketahui';
        if (!cityGrouped[city]) {
          cityGrouped[city] = {
            city: city,
            branchCount: 0,
            totalWeight: 0,
            branches: []
          };
        }
        cityGrouped[city].branchCount += 1;
        cityGrouped[city].totalWeight += branch.totalWeight;
        cityGrouped[city].branches.push({
          id: branch.id,
          name: branch.name,
          weight: branch.totalWeight
        });
      });

      // Sort cities by total weight (descending - terbanyak dulu)
      const cityDataArray = Object.values(cityGrouped).sort((a, b) => b.totalWeight - a.totalWeight);

      setBranches(allBranches);
      setBranchData(branchesWithOffset);
      setCityData(cityDataArray);
    } catch (error) {
      console.error('Failed to fetch map data:', error);
      toast.error('Gagal memuat data peta');
    } finally {
      setLoading(false);
    }
  };

  const formatWeight = (weight) => {
    return `${Math.round(weight).toLocaleString('id-ID')} gr`;
  };

  const handleCityClick = (cityData) => {
    setSelectedCity(cityData.city);
    setSelectedCityBranches(cityData.branches);
    setDialogOpen(true);
  };


  // Style for GeoJSON polygons
  const geoJsonStyle = {
    fillColor: '#f0f9ff',
    fillOpacity: 0.3,
    color: '#0891b2',
    weight: 1,
    opacity: 0.8
  };

  // Highlight style on hover
  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: (e) => {
        const layer = e.target;
        layer.setStyle({
          fillColor: '#e0f2fe',
          fillOpacity: 0.5,
          weight: 2,
          color: '#0369a1'
        });
      },
      mouseout: (e) => {
        const layer = e.target;
        layer.setStyle(geoJsonStyle);
      }
    });

    // Show tooltip with region name
    if (feature.properties && feature.properties.Propinsi) {
      layer.bindTooltip(feature.properties.Propinsi, {
        permanent: false,
        direction: 'center',
        className: 'border-0 bg-white shadow-lg px-2 py-1 text-xs font-medium'
      });
    }
  };

  // Custom marker icon using manager profile photo
  const getMarkerIcon = (branch) => {
    // Use manager's S3 photo URL if available
    const photoUrl = branch.manager_photo_url;
    
    if (photoUrl) {
      return L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 3px solid #0d9488;
            box-shadow: 0 3px 8px rgba(0,0,0,0.4);
            overflow: hidden;
            background: white;
          ">
            <img src="${photoUrl}" style="
              width: 100%;
              height: 100%;
              object-fit: cover;
            " onerror="this.parentElement.innerHTML='<div style=&quot;width:100%;height:100%;background:#0d9488;color:white;display:flex;align-items:center;justify-content:center;font-size:20px;&quot;>📍</div>'"/>
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      });
    }
    
    // Fallback: default marker with building icon
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background-color: #0d9488;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 22px;
        ">🏢</div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>
          Peta Cabang Indonesia
        </h1>
        <p className="text-slate-600 mt-1">
          Visualisasi lokasi cabang dan data pembelian per kota
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Section */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="text-teal-600" size={24} />
                Peta Wilayah Indonesia
              </CardTitle>
              <p className="text-sm text-slate-500">Peta dengan batasan provinsi Indonesia</p>
            </CardHeader>
            <CardContent>
              <div style={{ height: '600px', width: '100%' }}>
                <MapContainer
                  center={INDONESIA_CENTER}
                  zoom={5}
                  minZoom={4}
                  maxZoom={10}
                  maxBounds={INDONESIA_BOUNDS}
                  maxBoundsViscosity={1.0}
                  style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Indonesia Province Boundaries */}
                  {geoJsonData && (
                    <GeoJSON 
                      data={geoJsonData} 
                      style={geoJsonStyle}
                      onEachFeature={onEachFeature}
                    />
                  )}

                  {/* Branch Markers */}
                  {branchData.map((branch) => (
                    <Marker
                      key={branch.id}
                      position={branch.offsetCoordinates || branch.coordinates}
                      icon={getMarkerIcon(branch)}
                    >
                      <Popup>
                        <div className="p-2 min-w-[220px]">
                          <h3 className="font-bold text-lg text-teal-700 mb-2">
                            {branch.name}
                          </h3>
                          {branch.isGrouped && (
                            <div className="mb-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                              📍 {branch.groupSize} cabang di lokasi ini
                            </div>
                          )}
                          
                          {/* Manager Profile Section */}
                          {branch.manager && (
                            <div className="mb-3 p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-200">
                              <div className="flex flex-col items-center gap-2">
                                {branch.manager_photo_url ? (
                                  <img 
                                    src={branch.manager_photo_url} 
                                    alt={branch.manager.name}
                                    className="w-32 h-40 rounded-lg object-cover border-2 border-teal-400 shadow-lg"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className={`w-32 h-40 rounded-lg bg-teal-500 text-white flex items-center justify-center text-4xl font-bold border-2 border-teal-400 shadow-lg ${branch.manager_photo_url ? 'hidden' : ''}`}
                                >
                                  {branch.manager.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="text-center">
                                  <div className="text-xs text-teal-600 font-medium">Mitra Pengelola</div>
                                  <div className="font-semibold text-slate-800 text-base">{branch.manager.name}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className="text-slate-500" />
                              <span className="text-slate-700">{branch.location || '-'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Package size={14} className="text-teal-500" />
                              <span className="font-semibold text-teal-700">
                                Total Pembelian: {formatWeight(branch.totalWeight)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Building2 size={14} className="text-slate-500" />
                              <span className="text-slate-600">
                                {branch.purchaseCount} transaksi
                              </span>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Summary Table */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="text-teal-600" size={24} />
                Data per Kota
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cityData.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MapPin size={48} className="mx-auto mb-3 opacity-20" />
                  <p>Belum ada cabang dengan lokasi</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kota</TableHead>
                        <TableHead className="text-right">Total Cabang</TableHead>
                        <TableHead className="text-right">Total Pembelian</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cityData.map((city, index) => (
                        <TableRow key={city.city} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className="text-teal-600" />
                              {city.city}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{city.branchCount}</TableCell>
                          <TableCell 
                            className="text-right font-semibold text-teal-700 cursor-pointer hover:text-teal-900 hover:underline"
                            onClick={() => handleCityClick(city)}
                          >
                            {formatWeight(city.totalWeight)}
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Total Footer Row */}
                      <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                        <TableCell className="font-bold text-blue-900">TOTAL</TableCell>
                        <TableCell className="text-right font-bold text-blue-900">
                          {cityData.reduce((sum, c) => sum + c.branchCount, 0)}
                        </TableCell>
                        <TableCell className="text-right font-bold text-blue-900">
                          {formatWeight(cityData.reduce((sum, c) => sum + c.totalWeight, 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Info Card */}
      {branches.length > branchData.length && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <MapPin className="text-yellow-600 mt-1" size={20} />
              <div className="flex-1">
                <p className="text-sm text-yellow-900 font-medium">
                  Informasi
                </p>
                <p className="text-sm text-yellow-800 mt-1">
                  {branches.length - branchData.length} cabang belum memiliki lokasi atau lokasi tidak dikenali.
                  Pastikan kota/kabupaten cabang sudah diisi dengan benar.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog for City Branches Detail */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="text-teal-600" size={20} />
              Cabang di {selectedCity}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nama Cabang</TableHead>
                  <TableHead className="text-right">Total Pembelian</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedCityBranches.map((branch, index) => (
                  <TableRow key={branch.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                    <TableCell className="font-medium">{branch.name}</TableCell>
                    <TableCell className="text-right font-semibold text-teal-700">
                      {formatWeight(branch.weight)}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                  <TableCell className="font-bold text-blue-900">TOTAL</TableCell>
                  <TableCell className="text-right font-bold text-blue-900">
                    {formatWeight(selectedCityBranches.reduce((sum, b) => sum + b.weight, 0))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Peta;
